import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import { ViewColumnsIcon } from '@heroicons/react/24/outline';
import { Pagination, Column, Table } from '@shared/components/Table';
import { SearchBar } from '@shared/components/SearchBar';
import { FilterBar } from '@shared/components/FilterBar';
import { FilterPopup } from '@shared/components/FilterPopup';
import {
  ColumnCustomizationPopup,
  type ColumnOption,
} from '@shared/components/ColumnCustomizationPopup';
import { ToggleButton } from '@shared/components/ToggleButton';
import { Tooltip } from '@shared/components/Tooltip';
import { Snackbar } from '@shared/components/Snackbar';
import { Menu, MenuVariant, MenuSize } from '@shared/components/Menu';
import { UpdateStatusDialog } from '@/epics/adminManagment/pages/adminList/UpdateStatusDialog';
import { getTechnicianStatusDialogConfig } from './technicianStatusDialogConfig';
import { updateTechnicianStatusApiService } from '@epics/technicianManagement/api/updateTechnicianStatusApiService';
import { useTechnicianList } from './useTechnicianList';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import ErrorPage403 from '@/app/routes/errorPage403';
import type { TechnicianListItem } from '@epics/technicianManagement/types/technicianListType';
import { TECHNICIAN_MESSAGES } from '@epics/technicianManagement/constants/messages';
import { TECHNICIAN_ERROR_MESSAGES } from '@epics/technicianManagement/errors/errorMessages';
import noDataAnimation from '@/assets/lottie/noDataAnimation.json';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const PLACEHOLDER_NA = '—';

const NAME_TRUNCATE_LENGTH = 30;

function formatNameForDisplay(name: string): string {
  if (name.length <= NAME_TRUNCATE_LENGTH) {
    return name;
  }
  return `${name.slice(0, NAME_TRUNCATE_LENGTH)}…`;
}

const COLUMN_OPTIONS: ColumnOption[] = [
  { key: 'name', label: 'Imię i nazwisko', disabled: true },
  { key: 'technician_id', label: 'ID technika', disabled: true },
  { key: 'email', label: 'Adres e-mail' },
  { key: 'phone', label: 'Numer telefonu' },
  { key: 'availability', label: 'Aktualna dostępność', disabled: true },
  { key: 'status', label: 'Status', disabled: true },
];

const DEFAULT_VISIBLE_COLUMNS: Record<string, boolean> = Object.fromEntries(
  COLUMN_OPTIONS.map((o) => [o.key, true])
);

const COLUMNS_URL_PARAM = 'cols';

/** Comma-separated list of visible column keys when all are visible. */
const DEFAULT_COLS_STRING = COLUMN_OPTIONS.map((o) => o.key).join(',');

function parseColsParam(value: string | null): Record<string, boolean> | null {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const validKeys = new Set(COLUMN_OPTIONS.map((o) => o.key));
  const visibleKeys = value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => validKeys.has(s));
  if (visibleKeys.length === 0) {
    return null;
  }
  const result: Record<string, boolean> = {};
  COLUMN_OPTIONS.forEach((o) => {
    result[o.key] = visibleKeys.includes(o.key);
  });
  return result;
}

function buildColsParam(visibleKeys: Record<string, boolean>): string {
  return COLUMN_OPTIONS.filter((o) => visibleKeys[o.key] !== false)
    .map((o) => o.key)
    .join(',');
}

/** Get profile image URL from row; profile can be [] or object, object may have url. */
function getProfileImageUrl(
  profile: TechnicianListItem['profile']
): string | undefined {
  if (Array.isArray(profile) || !profile || !('url' in profile)) {
    return undefined;
  }
  const url = (profile as { url?: string }).url;
  return typeof url === 'string' && url.trim() !== '' ? url : undefined;
}

export default function TechnicianList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [
    showAddTechnicianSuccessSnackbar,
    setShowAddTechnicianSuccessSnackbar,
  ] = useState(false);
  const [
    showEditTechnicianSuccessSnackbar,
    setShowEditTechnicianSuccessSnackbar,
  ] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTechnician, setSelectedTechnician] =
    useState<TechnicianListItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStatusSuccessSnackbar, setShowStatusSuccessSnackbar] =
    useState(false);
  const [statusSuccessMessage, setStatusSuccessMessage] = useState('');
  const [showStatusErrorSnackbar, setShowStatusErrorSnackbar] = useState(false);
  const [statusErrorMessage, setStatusErrorMessage] = useState('');
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);
  const [isColumnPopupOpen, setIsColumnPopupOpen] = useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<
    Record<string, boolean>
  >(() => ({ ...DEFAULT_VISIBLE_COLUMNS }));

  // Restore or reset column visibility from URL
  const colsParam = searchParams.get(COLUMNS_URL_PARAM);
  useEffect(() => {
    const parsed = parseColsParam(colsParam);
    if (parsed) {
      setVisibleColumnKeys(parsed);
    } else {
      setVisibleColumnKeys({ ...DEFAULT_VISIBLE_COLUMNS });
    }
  }, [colsParam]);

  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const columnContainerRef = useRef<HTMLDivElement | null>(null);

  const MIN_SPACE_BELOW_FOR_MENU = 70;

  const { privileges, isLoading: isPrivilegesLoading } = useUserPrivileges();

  const {
    technicians,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing,
    setSearch,
    statusOptions,
    setStatusOptions,
    availabilityOptions,
    setAvailabilityOptions,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    refetchTechnicians,
    accessError,
  } = useTechnicianList();

  useEffect(() => {
    if (location.state?.showAddTechnicianSuccess) {
      setShowAddTechnicianSuccessSnackbar(true);
      window.history.replaceState({}, document.title);
    }
    if (location.state?.showEditTechnicianSuccess) {
      setShowEditTechnicianSuccessSnackbar(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (openMenuRowId === null) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(e.target as Node)
      ) {
        setOpenMenuRowId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuRowId]);

  const prevOpenMenuRowIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (openMenuRowId !== null) {
      prevOpenMenuRowIdRef.current = openMenuRowId;
      const anchor = menuAnchorRef.current;
      if (anchor) {
        const firstMenuitem =
          anchor.querySelector<HTMLElement>('[role="menuitem"]');
        if (firstMenuitem) {
          requestAnimationFrame(() => firstMenuitem.focus());
        }
      }
    } else {
      const hadOpenMenu = prevOpenMenuRowIdRef.current !== null;
      prevOpenMenuRowIdRef.current = null;
      if (hadOpenMenu && menuTriggerRef.current) {
        requestAnimationFrame(() => menuTriggerRef.current?.focus());
      }
    }
  }, [openMenuRowId]);

  const columns: Column<TechnicianListItem>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Imię i nazwisko',
        minWidth: '120px',
        render: (row) => {
          const profileImageUrl = getProfileImageUrl(row.profile);
          return (
            <div className="flex items-center w-full min-w-0 gap-3">
              <div className="w-8 h-8 rounded-full bg-[#E8EEF0] flex items-center justify-center shrink-0 overflow-hidden">
                {profileImageUrl ? (
                  <img
                    src={profileImageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    role="img"
                    aria-label={row.name}
                  />
                ) : (
                  <span className="text-[#223746] text-base font-medium">
                    {getInitials(row.name)}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <Tooltip content={row.name}>
                  <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                    {formatNameForDisplay(row.name)}
                  </span>
                </Tooltip>
              </div>
            </div>
          );
        },
      },
      {
        key: 'technician_id',
        header: 'ID technika',
        minWidth: '100px',
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
            {row.technician_id}
          </span>
        ),
      },
      {
        key: 'email',
        header: 'Adres e-mail',
        minWidth: '180px',
        render: (row) => (
          <Tooltip content={row.email}>
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {row.email}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'phone',
        header: 'Numer telefonu',
        minWidth: '120px',
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
            {row.phone_number}
          </span>
        ),
      },
      {
        key: 'availability',
        header: 'Aktualna dostępność',
        width: '200px',
        render: () => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
            {PLACEHOLDER_NA}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: '120px',
        render: (row, index) => {
          const statusToggle = (
            <div
              data-testid={`toggle-status-${index}`}
              onClick={(e) => {
                if (!privileges.technicianActivateDeactivate) {
                  return;
                }
                e.stopPropagation();
                setSelectedTechnician(row);
                setIsDialogOpen(true);
              }}
              className={
                privileges.technicianActivateDeactivate
                  ? 'cursor-pointer inline-flex'
                  : 'cursor-not-allowed inline-flex opacity-50'
              }
            >
              <div style={{ pointerEvents: 'none' }}>
                <ToggleButton
                  checked={row.status === 'active'}
                  onChange={() => {}}
                  label={row.status === 'active' ? 'Aktywny' : 'Nieaktywny'}
                  size="Small"
                  disabled={!privileges.technicianActivateDeactivate}
                />
              </div>
            </div>
          );

          if (privileges.technicianActivateDeactivate) {
            return statusToggle;
          }

          return (
            <Tooltip
              content={TECHNICIAN_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
              side="left"
              forceShow
              triggerWrapContent
            >
              {statusToggle}
            </Tooltip>
          );
        },
      },
      {
        key: 'actions',
        header: 'Akcje',
        width: '60px',
        stickyRight: true,
        stickyRightBg: '#ffffff',
        render: (row, index) => {
          const isOpen = openMenuRowId === row.id;
          return (
            <div
              ref={isOpen ? menuAnchorRef : undefined}
              className="relative inline-flex items-center justify-center w-full"
            >
              <button
                ref={(el) => {
                  if (isOpen) {
                    menuTriggerRef.current = el;
                  }
                }}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = openMenuRowId !== row.id;
                  if (willOpen) {
                    menuTriggerRef.current = e.currentTarget;
                    const buttonRect = (
                      e.currentTarget as HTMLButtonElement
                    ).getBoundingClientRect();
                    const containerRect =
                      tableScrollContainerRef.current?.getBoundingClientRect();
                    const visibleBottom = containerRect
                      ? containerRect.bottom
                      : window.innerHeight;
                    const spaceBelow = visibleBottom - buttonRect.bottom;
                    setMenuOpensUpward(spaceBelow < MIN_SPACE_BELOW_FOR_MENU);
                  }
                  setOpenMenuRowId((prev) => (prev === row.id ? null : row.id));
                }}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-label={`Akcje dla technika ${row.name}`}
                data-testid={`action-menu-button-${index ?? 0}`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {isOpen && (
                <div
                  className={`absolute right-0 z-50 ${menuOpensUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                  role="presentation"
                  onKeyDown={(e) => {
                    if (e.key !== 'Tab') {
                      return;
                    }
                    const anchor = menuAnchorRef.current;
                    if (!anchor) {
                      return;
                    }
                    const items =
                      anchor.querySelectorAll<HTMLElement>('[role="menuitem"]');
                    const lastItem = items[items.length - 1];
                    if (
                      lastItem &&
                      document.activeElement === lastItem &&
                      !e.shiftKey
                    ) {
                      setOpenMenuRowId(null);
                    }
                  }}
                >
                  <Menu
                    items={[
                      {
                        id: 'VIEW',
                        label: 'Podgląd',
                        disabled: !privileges.technicianView,
                        disabledTooltip:
                          TECHNICIAN_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
                        disabledTooltipSide: 'left',
                      },
                      {
                        id: 'EDIT',
                        label: 'Edytuj',
                        disabled: !privileges.technicianEdit,
                        disabledTooltip:
                          TECHNICIAN_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
                        disabledTooltipSide: 'left',
                      },
                    ]}
                    variant={MenuVariant.Light}
                    size={MenuSize.Small}
                    toolTip={false}
                    onItemClick={(itemId) => {
                      setOpenMenuRowId(null);
                      if (itemId === 'VIEW') {
                        navigate(`/technicians/Podgląd-technika/${row.id}`);
                      } else if (itemId === 'EDIT') {
                        navigate(`/technicians/Edycja-technika/${row.id}`);
                      }
                    }}
                    showLeadingIcons={false}
                  />
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [
      openMenuRowId,
      menuOpensUpward,
      navigate,
      setSelectedTechnician,
      setIsDialogOpen,
      privileges,
    ]
  );

  const visibleColumns = useMemo(
    () =>
      columns.filter(
        (col) => col.key === 'actions' || visibleColumnKeys[col.key] !== false
      ),
    [columns, visibleColumnKeys]
  );

  const handleApplyFiltersAndClose = () => {
    handleApplyFilters();
    setIsFilterOpen(false);
  };

  const handleCloseFilter = useCallback(() => {
    setStatusOptions(appliedFilters.statusOptions);
    setAvailabilityOptions(appliedFilters.availabilityOptions);
    setIsFilterOpen(false);
  }, [appliedFilters, setAvailabilityOptions, setStatusOptions]);

  const handleCloseColumnPopup = () => {
    setIsColumnPopupOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        isFilterOpen &&
        filterContainerRef.current &&
        !filterContainerRef.current.contains(target) &&
        !(target as Element).closest?.('[data-date-picker-portal]')
      ) {
        handleCloseFilter();
      }
      if (
        isColumnPopupOpen &&
        columnContainerRef.current &&
        !columnContainerRef.current.contains(target)
      ) {
        handleCloseColumnPopup();
      }
    };
    if (isFilterOpen || isColumnPopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFilterOpen, isColumnPopupOpen, handleCloseFilter]);

  const handleColumnVisibilityChange = (key: string, visible: boolean) => {
    const nextVisible = { ...visibleColumnKeys, [key]: visible };
    setVisibleColumnKeys(nextVisible);
    const colsString = buildColsParam(nextVisible);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (colsString === DEFAULT_COLS_STRING) {
          next.delete(COLUMNS_URL_PARAM);
        } else {
          next.set(COLUMNS_URL_PARAM, colsString);
        }
        return next;
      },
      { replace: true }
    );
  };

  const handleDialogClose = () => {
    if (!isSubmitting) {
      setIsDialogOpen(false);
      setSelectedTechnician(null);
    }
  };

  const handleConfirmStatusUpdate = async () => {
    if (!selectedTechnician) {
      return;
    }

    setIsSubmitting(true);
    const isActive = selectedTechnician.status === 'inactive';

    try {
      const result =
        await updateTechnicianStatusApiService.updateTechnicianStatus({
          technicianId: selectedTechnician.id,
          isActive,
        });

      if (result.success) {
        const message = isActive
          ? 'Technik został aktywowany.'
          : 'Technik został dezaktywowany.';
        setStatusSuccessMessage(message);
        setShowStatusSuccessSnackbar(true);
        setIsDialogOpen(false);
        setSelectedTechnician(null);
        refetchTechnicians();
      } else {
        setStatusErrorMessage(
          result.error || 'Wystąpił błąd podczas aktualizacji statusu.'
        );
        setShowStatusErrorSnackbar(true);
        setIsDialogOpen(false);
        setSelectedTechnician(null);
        refetchTechnicians();
      }
    } catch (_error) {
      setStatusErrorMessage(
        'Wystąpił nieoczekiwany błąd podczas aktualizacji statusu.'
      );
      setShowStatusErrorSnackbar(true);
      setIsDialogOpen(false);
      setSelectedTechnician(null);
      refetchTechnicians();
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageOutOfRange =
    !loading && totalCount > 0 && page > Math.ceil(totalCount / limit);

  const hasNoData =
    !loading &&
    !isSearchClearing &&
    !pageOutOfRange &&
    page === 1 &&
    technicians.length === 0 &&
    totalCount === 0 &&
    isNoFilterFetch &&
    !search.trim() &&
    !appliedFilters.statusOptions.some((opt) => opt.checked) &&
    !appliedFilters.availabilityOptions.some((opt) => opt.checked);

  if (!isPrivilegesLoading && !privileges.technicianList) {
    return <ErrorPage403 />;
  }
  if (accessError) {
    return <ErrorPage403 />;
  }

  return (
    <>
      <div className="w-full h-full min-h-0 flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden">
        <div
          className="flex items-center justify-between shrink-0"
          id="technician-list-content"
        >
          <div className="text-[#181A1B] text-[22px] font-normal leading-7">
            Technicy
          </div>
          <div className="flex flex-row gap-2">
            <button
              onClick={
                hasNoData
                  ? undefined
                  : () => navigate('/technicians/Zobacz-kalendarz')
              }
              disabled={hasNoData}
              className={`h-10 bg-transparent px-4 py-2.5 border flex items-center justify-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] ${
                hasNoData
                  ? 'border-[#DCDDE0] opacity-40 cursor-not-allowed'
                  : 'border-[#DCDDE0] hover:bg-neutral-95 hover:border-neutral-70 cursor-pointer'
              }`}
              aria-label="View Calendar"
            >
              <span className="text-on-surface-variant text-sm font-medium leading-5 tracking-[0.1px]">
                View Calendar
              </span>
            </button>
            {privileges.technicianCreate ? (
              <button
                onClick={() => navigate('/technicians/addTechnician')}
                className="h-10 bg-[#2A4455] px-4 py-2.5 flex items-center justify-center rounded-lg hover:bg-[#1f3340] transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                aria-label="Dodaj technika"
              >
                <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                  Dodaj technika
                </span>
              </button>
            ) : (
              <Tooltip
                content={TECHNICIAN_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
                side="bottom"
                forceShow
                triggerWrapContent
              >
                <button
                  disabled
                  className="h-10 bg-[#B0BEC5] px-4 py-2.5 flex items-center justify-center rounded-lg cursor-not-allowed"
                  aria-label="Dodaj technika"
                >
                  <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                    Dodaj technika
                  </span>
                </button>
              </Tooltip>
            )}
          </div>
        </div>

        {!hasNoData && (
          <div className="flex items-center justify-between shrink-0">
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Szukaj po nazwie lub ID"
            />
            <div className="flex items-center gap-2">
              <div
                ref={filterContainerRef}
                className={`dropdown ${isFilterOpen ? 'show' : ''}`}
              >
                <style>{`
                    .dropdown {
                      position: relative;
                    }
                    .dropdown-content {
                      position: absolute;
                      background-color: #f9f9f9;
                      min-width: 130px;
                      box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
                      padding: 12px 16px;
                    }
                    .dropdown.show .dropdown-content {
                      display: block;
                    }
                  `}</style>
                <FilterBar
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  isOpen={isFilterOpen}
                  filterCount={
                    appliedFilters.statusOptions.filter((opt) => opt.checked)
                      .length +
                    appliedFilters.availabilityOptions.filter(
                      (opt) => opt.checked
                    ).length
                  }
                />
                <FilterPopup
                  isOpen={isFilterOpen}
                  onClose={handleCloseFilter}
                  statusOptions={statusOptions}
                  onStatusChange={setStatusOptions}
                  availabilityOptions={availabilityOptions}
                  onAvailabilityChange={setAvailabilityOptions}
                  showDateRange={false}
                  onApply={handleApplyFiltersAndClose}
                  onClear={handleClearFilters}
                />
              </div>
              <div ref={columnContainerRef} className="relative">
                <button
                  type="button"
                  onClick={() => {
                    handleCloseFilter();
                    setIsColumnPopupOpen((prev) => !prev);
                  }}
                  className="flex items-center justify-center rounded-lg border border-[#DCDDE0] px-[8px] py-[8px] cursor-pointer relative overflow-visible transition-all duration-200 ease-in-out hover:bg-[#E8EEF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                  aria-label="Kolumny"
                  aria-expanded={isColumnPopupOpen}
                  aria-haspopup="dialog"
                  data-testid="column-customization-button"
                >
                  <ViewColumnsIcon
                    className="h-6 w-6 text-on-surface-variant shrink-0"
                    aria-hidden
                  />
                </button>
                <ColumnCustomizationPopup
                  isOpen={isColumnPopupOpen}
                  onClose={handleCloseColumnPopup}
                  columnOptions={COLUMN_OPTIONS}
                  visibleKeys={visibleColumnKeys}
                  onVisibilityChange={handleColumnVisibilityChange}
                />
              </div>
            </div>
          </div>
        )}

        <div
          ref={tableScrollContainerRef}
          className="flex-1 min-h-0 overflow-x-auto overflow-y-auto"
        >
          {technicians.length === 0 && !loading && !isSearchClearing ? (
            hasNoData ? (
              <div className="flex flex-col items-center justify-center w-full h-full">
                <LottieAnimation
                  animationData={noDataAnimation}
                  width={277}
                  aspectRatio="1 / 1"
                />
                <div className="flex flex-col items-center justify-center gap-5">
                  <div className="flex flex-col items-start justify-start gap-1">
                    <div className="w-[548px] text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                      Brak techników.
                    </div>
                    <div className="w-[548px] text-center text-[#474A4C] text-base font-normal leading-6 tracking-[0.5px]">
                      Technicy pojawią się tutaj po dodaniu ich do systemu.
                    </div>
                  </div>
                  {privileges.technicianCreate ? (
                    <button
                      onClick={() => navigate('/technicians/addTechnician')}
                      className="h-10 bg-[#2A4455] px-4 py-2.5 cursor-pointer rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center"
                    >
                      <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                        Dodaj technika
                      </span>
                    </button>
                  ) : (
                    <Tooltip
                      content={TECHNICIAN_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
                      side="bottom"
                      forceShow
                      triggerWrapContent
                    >
                      <button
                        disabled
                        className="h-10 bg-[#B0BEC5] px-4 py-2.5 rounded-lg cursor-not-allowed flex items-center justify-center"
                      >
                        <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                          Dodaj technika
                        </span>
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center w-full h-full gap-3">
                <LottieAnimation
                  animationData={noResultsAnimation}
                  width="clamp(160px, 18vw, 360px)"
                  aspectRatio="432 / 350"
                />
                <div className="w-[548px] max-w-full h-12 flex flex-col items-center justify-start gap-1">
                  <div className="w-full h-6 text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                    Nie znaleziono wyników.
                  </div>
                  <div className="w-full h-5 text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]">
                    {(() => {
                      const hasSearch = search.trim() !== '';
                      const hasFilter =
                        appliedFilters.statusOptions.some(
                          (opt) => opt.checked
                        ) ||
                        appliedFilters.availabilityOptions.some(
                          (opt) => opt.checked
                        );
                      if (hasSearch && hasFilter) {
                        return 'Spróbuj zmienić kryteria wyszukiwania lub filtry.';
                      }
                      if (hasSearch) {
                        return 'Spróbuj zmienić kryteria wyszukiwania.';
                      }
                      if (hasFilter) {
                        return 'Spróbuj zmienić ustawienia filtrów.';
                      }
                      return 'Spróbuj zmienić kryteria wyszukiwania lub filtry.';
                    })()}
                  </div>
                </div>
              </div>
            )
          ) : (
            <Table
              columns={visibleColumns}
              data={technicians}
              loading={loading}
              loadingRows={limit}
              stickyHeader
              getRowKey={(row) => row.id}
              getStickyCellZIndex={(row, _idx, columnKey) =>
                columnKey === 'actions' && openMenuRowId === row.id ? 50 : 1
              }
            />
          )}
        </div>

        {/* Pagination - Fixed at bottom */}
        {!hasNoData && totalCount > 10 && (
          <div className="shrink-0 px-4 -mx-4">
            <Pagination
              page={page}
              limit={limit}
              totalCount={totalCount}
              onPageChange={setPage}
              onLimitChange={handleLimitChange}
            />
            {limitError && (
              <div className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
                {limitError}
              </div>
            )}
          </div>
        )}

        <Snackbar
          open={showAddTechnicianSuccessSnackbar}
          message={TECHNICIAN_MESSAGES.SUCCESS_ADD}
          variant="success"
          onClose={() => setShowAddTechnicianSuccessSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton={true}
        />

        <Snackbar
          open={showEditTechnicianSuccessSnackbar}
          message={TECHNICIAN_MESSAGES.SUCCESS_EDIT}
          variant="success"
          onClose={() => setShowEditTechnicianSuccessSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton={true}
        />

        <Snackbar
          open={showStatusSuccessSnackbar}
          message={statusSuccessMessage}
          variant="success"
          onClose={() => setShowStatusSuccessSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton={true}
        />

        <Snackbar
          open={showStatusErrorSnackbar}
          message={statusErrorMessage}
          variant="error"
          onClose={() => setShowStatusErrorSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton={true}
        />

        {selectedTechnician && (
          <UpdateStatusDialog
            open={isDialogOpen}
            onClose={handleDialogClose}
            onConfirm={handleConfirmStatusUpdate}
            isSubmitting={isSubmitting}
            {...getTechnicianStatusDialogConfig(
              selectedTechnician.name,
              selectedTechnician.status === 'active'
            )}
          />
        )}
      </div>
    </>
  );
}
